import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Select con búsqueda — menú siempre fondo blanco / texto negro (legible en shell oscuro).
 */
export default function SearchableSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Buscar…',
  emptyLabel = 'Sin resultados',
  required = false,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value],
  );

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else if (!value) setQuery('');
  }, [value, selected?.label]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 40);
    return options
      .filter((o) => `${o.label} ${o.hint || ''}`.toLowerCase().includes(q))
      .slice(0, 40);
  }, [options, query]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (opt) => {
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
  };

  return (
    <div className="ui-field relative" ref={wrapRef}>
      {label ? <span className="ui-field__label">{label}</span> : null}
      <input
        type="search"
        className="ui-search-input"
        value={query}
        disabled={disabled}
        required={required && !value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onChange('');
        }}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <ul className="ui-select-menu" role="listbox">
          {filtered.map((opt) => {
            const active = String(opt.value) === String(value);
            return (
              <li key={String(opt.value)} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`ui-select-option${active ? ' is-active' : ''}`}
                  onClick={() => pick(opt)}
                >
                  <span className="ui-select-option__label">{opt.label}</span>
                  {opt.hint ? (
                    <span className="ui-select-option__hint">{opt.hint}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {open && query.trim() && filtered.length === 0 && (
        <p className="ui-select-empty">{emptyLabel}</p>
      )}
    </div>
  );
}
